/**
 * Standalone entry point: mounts Villamine AI into any page.
 *
 *   const handle = mountVillamineAI({ apiBaseUrl: "https://ai.villamine.com" });
 *   handle.open(); handle.close(); handle.unmount();
 *
 * The widget is rendered inside an open Shadow DOM on a container that is
 * appended to <body>, i.e. outside villamine.com's <form runat="server">.
 * It contains no <form> element itself, so Enter never submits the host page.
 */
import { createRoot, type Root } from "react-dom/client";
import { resolveConfig, type VillamineAIOptions } from "./config";
import { WIDGET_CSS } from "./styles";
import { VillamineAI, type WidgetController } from "./VillamineAI";

export interface VillamineAIHandle {
  container: HTMLElement;
  root: ShadowRoot | HTMLElement;
  open(): void;
  close(): void;
  unmount(): void;
  send(message: string): void;
  setInput(text: string): void;
}

export const CONTAINER_ID = "villamine-ai-root";

/** One widget per document: a second mount replaces the first instead of duplicating it. */
const mounted = new WeakMap<Document, VillamineAIHandle>();

export function mountVillamineAI(options: VillamineAIOptions = {}): VillamineAIHandle {
  const doc = options.document ?? (typeof document !== "undefined" ? document : null);
  if (!doc) throw new Error("mountVillamineAI needs a document");
  const previous = mounted.get(doc);
  if (previous) {
    console.warn("[villamine-ai] already mounted; replacing the previous instance");
    previous.unmount();
  }
  const config = resolveConfig(options, doc);

  let container = options.container ?? null;
  const created = !container;
  if (!container) {
    container = doc.createElement("div");
    container.id = CONTAINER_ID;
  }
  container.setAttribute("data-villamine-ai", "");
  if (!container.isConnected) doc.body.appendChild(container);

  const useShadow = options.shadow !== false && typeof container.attachShadow === "function";
  const root: ShadowRoot | HTMLElement = useShadow ? (container.shadowRoot ?? container.attachShadow({ mode: "open" })) : container;

  const style = doc.createElement("style");
  style.setAttribute("data-villamine-ai-style", "");
  style.textContent = WIDGET_CSS;
  root.appendChild(style);

  const mountNode = doc.createElement("div");
  mountNode.className = "vmai-root";
  root.appendChild(mountNode);

  let controller: WidgetController | null = null;
  const reactRoot: Root = createRoot(mountNode);
  reactRoot.render(
    <VillamineAI
      config={config}
      onController={(ctrl) => {
        controller = ctrl;
      }}
    />,
  );

  let unmounted = false;
  const handle: VillamineAIHandle = {
    container,
    root,
    open: () => controller?.setOpen(true),
    close: () => controller?.setOpen(false),
    send: (message: string) => {
      controller?.setOpen(true);
      controller?.send(message);
    },
    setInput: (text: string) => controller?.setInput(text),
    unmount: () => {
      if (unmounted) return;
      unmounted = true;
      if (mounted.get(doc) === handle) mounted.delete(doc);
      if (activeHandle === handle) activeHandle = null;
      reactRoot.unmount();
      style.remove();
      mountNode.remove();
      if (created) container?.remove();
      else container?.removeAttribute("data-villamine-ai");
    },
  };
  mounted.set(doc, handle);
  activeHandle = handle;
  if (typeof window !== "undefined") {
    window.__villamineAI = handle;
  }
  return handle;
}

let activeHandle: VillamineAIHandle | null = null;

declare global {
  interface Window {
    VillamineAI?: {
      mount: typeof mountVillamineAI;
      open?: () => void;
      close?: () => void;
      send?: (message: string) => void;
      setInput?: (text: string) => void;
      getActiveHandle?: () => VillamineAIHandle | null;
    };
    __villamineAI?: VillamineAIHandle;
  }
}

/** Script-tag usage: window.VillamineAI.mount({...}). */
export function registerGlobal(): void {
  if (typeof window !== "undefined") {
    window.VillamineAI = {
      mount: (options) => {
        const handle = mountVillamineAI(options);
        activeHandle = handle;
        window.__villamineAI = handle;
        return handle;
      },
      open: () => activeHandle?.open(),
      close: () => activeHandle?.close(),
      send: (message: string) => activeHandle?.send(message),
      setInput: (text: string) => activeHandle?.setInput(text),
      getActiveHandle: () => activeHandle,
    };
  }
}
